import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    Briefcase, ChevronRight, ImagePlus, LayoutGrid, LucideIcon, Megaphone, MessagesSquare, PackagePlus, Search, Send, Sofa, Sparkles, Target,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { HomeSummary, useHomeApi } from '../../../common/hooks/use-home-api.hook';
import { KpiCard } from '../../../components/design-system/admin/KpiCard';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { formatCurrency, formatNumber } from '../../../components/design-system/admin/format';
import { InsightList } from '../../../components/design-system/analytics/InsightList';
import { TaskItem, TaskList } from '../../../components/design-system/analytics/TaskList';
import { useAskAi } from '../../../modules/vendor/assistant/AskAiContext';
import { greetingKey } from '../../../modules/vendor/agents/home-feed';
import { actionRoute, findingText } from '../../../modules/vendor/insights/insight-presentation';
import { useWorkspaceStore } from '../../../modules/vendor/side-bar/workspace.store';
import { loadLayout, saveLayout, WidgetPlacement } from '../../../modules/vendor/dashboard/widgets/widget-layout';
import { OverviewWidgets } from './OverviewWidgets';
import {
    AttentionEntry,
    Formatters,
    WorkspaceLine,
    attentionItems,
    homeBrief,
    keyFigures,
    quickActions,
    visitSince,
    workspaceLines,
} from './overview-reading';

dayjs.extend(relativeTime);

const WORKSPACE_ICON: Record<WorkspaceLine['id'], LucideIcon> = {
    visualizer: Sofa,
    assistant: MessagesSquare,
    marketing: Megaphone,
    ads: Target,
    business: Briefcase,
};

const ACTION_ICON: Record<string, LucideIcon> = {
    PRODUCT: PackagePlus,
    IMAGE: ImagePlus,
    POST: Send,
    SEARCH: Search,
};

const FIGURE_GRID: Record<number, string> = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
    5: 'sm:grid-cols-3 lg:grid-cols-5',
};

const format: Formatters = {
    number: (value) => formatNumber(value),
    money: (amount, currency) => (currency ? formatCurrency(amount, currency, 0) : formatNumber(amount)),
};

/**
 * Home → Overview: how things stand across the workspaces, at a glance.
 *
 * A greeting, Ardi's brief of the last thirty days beside what needs the
 * vendor, one row of key numbers, the workspaces they use with one line each,
 * and shortcuts. That is the page. The vendor's own widgets are still here,
 * below it, for whoever placed some — and one quiet button away for whoever
 * has not. What it used to carry besides (the illustrated quick-start tiles,
 * the agents and setup cards, a second copy of the findings, tiles selling
 * workspaces outside the plan) lives on Ardi's home, or nowhere.
 */
export const Dashboard: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuthContext();
    const askAi = useAskAi();
    const homeApi = useHomeApi();
    const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
    const { analyticsEnabled } = useSelector((state: VendorState) => state.settings.theme);
    const measures = analyticsEnabled !== false;

    // Per user: colleagues at one vendor keep their own arrangements.
    const userId = (user as any)?._id || (user as any)?.id;

    const [summary, setSummary] = useState<HomeSummary | null>(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        homeApi.getSummary()
            .then((s) => { if (alive) setSummary(s); })
            .catch(() => { if (alive) setSummary(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When they were last here — the visit before this one.
    const since = useMemo(() => visitSince(userId), [userId]);

    // The vendor's own widgets, kept exactly as they arranged them.
    const [layout, setLayout] = useState<WidgetPlacement[]>(() => loadLayout(userId));
    useEffect(() => setLayout(loadLayout(userId)), [userId]);
    const persist = useCallback((next: WidgetPlacement[]) => {
        setLayout(next);
        saveLayout(userId, next);
    }, [userId]);
    const [picking, setPicking] = useState(false);

    const brief = homeBrief(summary, { measures, format });
    const attention = attentionItems(summary, (finding) => actionRoute(finding));
    const figures = keyFigures(summary, { measures, format });
    const goals = useWorkspaceStore((st) => st.goals);
    const lines = workspaceLines(summary, { measures, format, goals });
    const actions = quickActions(summary);

    const attentionText = (entry: AttentionEntry) => {
        if (entry.text.kind === 'key') return t(`HOME_OVERVIEW.${entry.text.key}`, entry.text.params);
        if (entry.text.kind === 'raw') return entry.text.text;
        const finding = entry.text.finding;
        return findingText(t as any, finding.title, { key: finding.i18n?.title, params: finding.i18n?.params });
    };

    const tasks: TaskItem[] = attention.map((entry) => ({
        id: entry.key,
        tone: entry.tone === 'good' ? 'neutral' : entry.tone,
        title: attentionText(entry),
        href: entry.route,
    }));

    const firstName = user?.firstname || '';

    return (
        <PageContainer breadcrumbs={[{ label: t('AGENTS.NAV.OVERVIEW', 'Overview') }]}>
            <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6">
                {/* ── Greeting ─────────────────────────────────────────── */}
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h1 className="font-heading text-[22px] font-medium tracking-tight">
                            {firstName
                                ? t(`HOME_OVERVIEW.GREETING.${greetingKey()}`, { name: firstName })
                                : t(`HOME_OVERVIEW.GREETING.${greetingKey()}_NO_NAME`)}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {since ? t('HOME_OVERVIEW.SUBTITLE_SINCE', { when: dayjs(since).fromNow() }) : t('HOME_OVERVIEW.SUBTITLE')}
                        </p>
                    </div>
                    {askAi && (
                        <Button variant="outline" size="sm" onClick={() => askAi.open()}>
                            <Sparkles />
                            {t('HOME_OVERVIEW.ASK_ARDI')}
                        </Button>
                    )}
                </div>

                {/* ── Ardi's brief, beside what needs the vendor ──────── */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                    <div className="lg:col-span-3">
                        {loading ? (
                            <Skeleton className="h-56 w-full rounded-xl" />
                        ) : brief.length ? (
                            <InsightList
                                className="h-full"
                                title={t('HOME_OVERVIEW.BRIEF.TITLE')}
                                description={t('HOME_OVERVIEW.BRIEF.PERIOD')}
                                testId="overview-brief"
                                insights={brief.map((item) => ({
                                    id: item.id,
                                    tone: item.tone,
                                    title: t(`HOME_OVERVIEW.${item.title}`, item.params),
                                    detail: item.detail ? t(`HOME_OVERVIEW.${item.detail}`, item.params) : undefined,
                                    // A link only where there is something to do; the good news needs none.
                                    action: item.href && (item.tone === 'watch' || item.tone === 'bad' || item.id === 'quiet')
                                        ? { label: t('HOME_OVERVIEW.OPEN'), href: item.href }
                                        : undefined,
                                }))}
                            />
                        ) : (
                            <SectionCard className="h-full" title={t('HOME_OVERVIEW.BRIEF.TITLE')}>
                                <p className="text-sm text-muted-foreground">{t('HOME_OVERVIEW.BRIEF.FAILED')}</p>
                            </SectionCard>
                        )}
                    </div>
                    <div className="lg:col-span-2">
                        <TaskList
                            className="h-full"
                            title={t('HOME_OVERVIEW.NEEDS.TITLE')}
                            items={tasks}
                            loading={loading}
                            max={4}
                            emptyTitle={t('HOME_OVERVIEW.NEEDS.EMPTY_TITLE')}
                            emptyText={t('HOME_OVERVIEW.NEEDS.EMPTY_TEXT')}
                            more={{ label: (hidden) => t('HOME_OVERVIEW.NEEDS.MORE', { count: hidden }), href: '/home/dashboard' }}
                            testId="overview-needs-you"
                        />
                    </div>
                </div>

                {/* ── Key numbers ──────────────────────────────────────── */}
                {loading ? (
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
                    </div>
                ) : figures.length > 0 && (
                    <section aria-label={t('HOME_OVERVIEW.FIGURES.TITLE')} className={`grid grid-cols-2 gap-4 ${FIGURE_GRID[figures.length] ?? FIGURE_GRID[5]}`}>
                        {figures.map((figure) => (
                            <KpiCard
                                key={figure.key}
                                label={t(`HOME_OVERVIEW.FIGURES.${figure.label}`)}
                                value={figure.value}
                                delta={figure.delta}
                                hint={figure.delta ? t('HOME_OVERVIEW.FIGURES.VS_BEFORE') : t('HOME_OVERVIEW.FIGURES.LAST_30')}
                                href={figure.href}
                            />
                        ))}
                    </section>
                )}

                {/* ── Workspaces and shortcuts ─────────────────────────── */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                    <SectionCard className="lg:col-span-3" title={t('HOME_OVERVIEW.WORKSPACES.TITLE')} flush>
                        {loading ? (
                            <div className="flex flex-col gap-2 px-6 pb-6">
                                <Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" />
                            </div>
                        ) : (
                            <ul className="divide-y border-t" data-testid="overview-workspaces">
                                {lines.map((line) => {
                                    const Icon = WORKSPACE_ICON[line.id];
                                    return (
                                        <li key={line.id}>
                                            <Link
                                                to={line.href}
                                                onClick={() => setWorkspace(line.id)}
                                                className="group/ws flex items-center gap-3 px-6 py-3 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
                                            >
                                                <span className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted/50">
                                                    <Icon className="size-4 text-muted-foreground" aria-hidden />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm font-medium">{t(`WORKSPACE.${line.id.toUpperCase()}`)}</span>
                                                    <span className="block truncate text-xs text-muted-foreground">
                                                        {t(`HOME_OVERVIEW.WORKSPACES.${line.line}`, line.params)}
                                                    </span>
                                                </span>
                                                <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover/ws:translate-x-0.5 rtl:rotate-180 rtl:group-hover/ws:-translate-x-0.5" />
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </SectionCard>

                    <SectionCard className="lg:col-span-2" title={t('HOME_OVERVIEW.QUICK.TITLE')}>
                        <div className="flex flex-col gap-2">
                            {actions.map((action) => {
                                const Icon = ACTION_ICON[action.key];
                                return (
                                    <Button key={action.key} variant="outline" className="h-10 justify-start gap-2.5 px-3" asChild>
                                        <Link to={action.href}>
                                            <Icon className="text-muted-foreground" />
                                            <span className="truncate">{t(`HOME_OVERVIEW.QUICK.${action.key}`)}</span>
                                        </Link>
                                    </Button>
                                );
                            })}
                        </div>
                    </SectionCard>
                </div>

                {/* ── The vendor's own widgets ─────────────────────────── */}
                {layout.length > 0 || picking ? (
                    <section className="flex flex-col gap-3">
                        {layout.length > 0 && (
                            <h2 className="text-sm font-medium">{t('HOME_OVERVIEW.WIDGETS.TITLE')}</h2>
                        )}
                        <OverviewWidgets layout={layout} onChange={persist} picking={picking} onPickingChange={setPicking} />
                    </section>
                ) : (
                    <div className="flex justify-center">
                        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setPicking(true)}>
                            <LayoutGrid />
                            {t('HOME_OVERVIEW.WIDGETS.ADD')}
                        </Button>
                    </div>
                )}
            </div>
        </PageContainer>
    );
};

export default Dashboard;
